import React, { useEffect, useRef, useState } from 'react'
import { Dialog } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import { DetailButton } from '../common/detail'
import NumberInput from '../common/NumberInput'
import { useNumberFormat } from '../../hooks/useNumberFormat'
import DocumentModal from './DocumentModal'
import AiMaturitySuggestionModal from './AiMaturitySuggestionModal'
import { analyzeProofApi } from '../../api/maturityScoringApi'
import { AnalyzeProofResponse } from '../../types/maturityScoringTypes'
import '../common/detail/detail.css'
import './MaturityScoringModal.css'

type DocLike = {
  originalName?: string
  path?: string
  mimeType?: string
} | null

type MaturityScoringModalProps = {
  open: boolean
  onClose: () => void
  title: string
  maturityData?: { AnswerScore?: number | string }
  onAnswerScoreChange: (value: number) => void
  onAdd?: (finalScore?: number) => void
  onEdit?: (finalScore?: number) => void
  mode?: 'add' | 'edit'
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  fileName?: string
  /** Fresh file selected in this session — required for AI analyze-proof */
  proofFile?: File | null
  subDomainId?: string
  existingDocument?: DocLike
  t: (key: string, options?: Record<string, unknown>) => string
  TargetScore?: number
}

const LEVEL_META = [
  { level: 1, key: '1' },
  { level: 2, key: '2' },
  { level: 3, key: '3' },
  { level: 4, key: '4' },
  { level: 5, key: '5' },
] as const

function clampScore(value: number, min: number, max: number) {
  if (Number.isNaN(value)) return min
  return Math.min(max, Math.max(min, value))
}

function roundScore(value: number) {
  return Math.round(value * 100) / 100
}

const AI_CHOICE_KEY = 'incyia.maturityAiChoice'

function readAiChoice() {
  try {
    return sessionStorage.getItem(AI_CHOICE_KEY)
  } catch {
    return null
  }
}

function isAbortError(err: unknown) {
  const error = err as { code?: string; name?: string }
  return error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError'
}

const MaturityScoringModal: React.FC<MaturityScoringModalProps> = ({
  open,
  onClose,
  title,
  maturityData = {},
  onAnswerScoreChange,
  onAdd,
  onEdit,
  mode = 'add',
  onFileChange,
  fileName,
  proofFile = null,
  subDomainId,
  existingDocument,
  t,
  TargetScore,
}) => {
  const min = 1
  const maxCap =
    typeof TargetScore === 'number' && TargetScore > 0
      ? Math.min(5, TargetScore)
      : 5
  const max = Math.max(min, maxCap)

  const raw = Number(maturityData?.AnswerScore)
  const score = clampScore(
    Number.isFinite(raw) && raw >= min ? raw : min,
    min,
    max
  )
  const [draft, setDraft] = useState(score)
  const fmt = useNumberFormat()
  const [viewOpen, setViewOpen] = useState(false)
  const [localProof, setLocalProof] = useState<File | null>(null)
  const [aiOpen, setAiOpen] = useState(false)
  const [aiPhase, setAiPhase] = useState<
    'choice' | 'analyzing' | 'ready' | 'error'
  >('choice')
  const analysisAbort = useRef<AbortController | null>(null)
  const [aiResult, setAiResult] = useState<AnalyzeProofResponse | null>(null)
  const [aiError, setAiError] = useState<string | null>(null)
  const [suggestedScore, setSuggestedScore] = useState(min)
  const [submitting, setSubmitting] = useState(false)

  const effectiveProof = localProof || proofFile || null
  const displayName =
    localProof?.name || fileName || existingDocument?.originalName || ''
  const canView = Boolean(existingDocument?.path || existingDocument?.originalName)

  useEffect(() => {
    if (!open) {
      analysisAbort.current?.abort()
      return
    }
    if (open) {
      const next = clampScore(
        Number.isFinite(raw) && raw >= min ? raw : min,
        min,
        max
      )
      setDraft(next)
      if (roundScore(next) !== roundScore(score)) {
        onAnswerScoreChange(roundScore(next))
      }
      setLocalProof(proofFile ?? null)
      setAiOpen(false)
      setAiResult(null)
      setAiError(null)
      setAiPhase('choice')
      setSubmitting(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, max, min])

  useEffect(() => {
    if (proofFile) setLocalProof(proofFile)
  }, [proofFile])

  const levelFloor = Math.min(5, Math.max(1, Math.floor(draft)))
  const levelKey = String(levelFloor)
  const pct = max > min ? ((draft - min) / (max - min)) * 100 : 0

  const setScore = (value: number) => {
    const next = roundScore(clampScore(value, min, max))
    setDraft(next)
    onAnswerScoreChange(next)
  }

  const handleProofInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    if (file) setLocalProof(file)
    onFileChange(e)
  }

  const finishSubmit = (finalScore?: number) => {
    setAiOpen(false)
    setSubmitting(false)
    if (typeof finalScore === 'number') {
      setScore(finalScore)
    }
    if (mode === 'edit') onEdit?.(finalScore)
    else onAdd?.(finalScore)
  }

  const handleAcceptAi = () => {
    finishSubmit(suggestedScore)
  }

  const handleKeepScore = () => {
    finishSubmit()
  }

  const rememberChoice = (choice: 'use' | 'skip') => {
    try {
      sessionStorage.setItem(AI_CHOICE_KEY, choice)
    } catch {
      /* private browsing can block storage; the choice still applies this time */
    }
  }

  const cancelAnalysis = () => {
    analysisAbort.current?.abort()
    analysisAbort.current = null
    setSubmitting(false)
    setAiOpen(false)
  }

  const startAnalysis = async () => {
    if (!effectiveProof) return
    analysisAbort.current?.abort()
    const controller = new AbortController()
    analysisAbort.current = controller

    setSubmitting(true)
    setAiOpen(true)
    setAiPhase('analyzing')
    setAiResult(null)
    setAiError(null)

    try {
      const id = String(subDomainId || '').trim()
      if (!id) {
        throw new Error(
          t('popups.aiMaturitySuggestion.missingSubDomain', {
            defaultValue: 'Subdomain is required for AI analysis.',
          })
        )
      }
      const result = await analyzeProofApi({
        proof: effectiveProof,
        SubDomainId: id,
        signal: controller.signal,
      })
      if (controller.signal.aborted) return
      const rawSuggested =
        typeof result.allowedSuggestedScore === 'number'
          ? result.allowedSuggestedScore
          : Number(result.suggestedScore)
      const nextSuggested = roundScore(clampScore(rawSuggested, min, max))
      setSuggestedScore(nextSuggested)
      setAiResult(result)
      setAiPhase('ready')
    } catch (err: unknown) {
      if (controller.signal.aborted || isAbortError(err)) return
      console.error('[analyze-proof] failed', err)
      const error = err as { message?: string; response?: { data?: { message?: string } } }
      setAiError(
        error?.message === 'ANALYSIS_UNREACHABLE'
          ? t('popups.aiMaturitySuggestion.serviceUnreachable')
          : error?.response?.data?.message ||
              error?.message ||
              t('popups.aiMaturitySuggestion.errorGeneric', {
                defaultValue: 'Analysis failed. Please try again later.',
              })
      )
      setAiPhase('error')
    } finally {
      if (!controller.signal.aborted) setSubmitting(false)
    }
  }

  const handleChooseAi = () => {
    rememberChoice('use')
    void startAnalysis()
  }

  const handleChooseSkip = () => {
    rememberChoice('skip')
    finishSubmit()
  }

  const handleSubmit = () => {
    if (submitting) return

    if (!effectiveProof) {
      if (displayName) {
        setAiOpen(true)
        setAiPhase('error')
        setAiError(
          t('popups.aiMaturitySuggestion.reselectProof', {
            defaultValue:
              'Please re-select the proof file, then click Add Maturity again.',
          })
        )
        return
      }
      finishSubmit()
      return
    }

    const remembered = readAiChoice()
    if (remembered === 'skip') {
      finishSubmit()
      return
    }
    if (remembered === 'use') {
      void startAnalysis()
      return
    }

    setAiResult(null)
    setAiError(null)
    setAiPhase('choice')
    setAiOpen(true)
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: 'incyia-modal-paper msm-paper' }}
      >
        <div className="msm">
          <div className="msm-head">
            <div>
              <p className="msm-eyebrow">
                {t('popups.addMaturityScoring.eyebrow', {
                  defaultValue: 'Maturity scoring',
                })}
              </p>
              <h2 className="msm-title">{title}</h2>
              <p className="msm-sub">
                {t('popups.addMaturityScoring.subtitle', {
                  defaultValue:
                    'Set a precise answer score between levels. Values are continuous — for example 1.00 to 1.99 within Level 1.',
                })}
              </p>
            </div>
            <button
              type="button"
              className="detail-icon-btn"
              onClick={onClose}
              aria-label={t('popups.addMaturityScoring.cancel')}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </button>
          </div>

          <div className="msm-score-card">
            <div className="msm-score-top">
              <span className="msm-score-label">
                {t('popups.addMaturityScoring.answerScore')}
              </span>
              <div className="msm-score-value">
                <NumberInput
                  className="msm-score-input"
                  min={min}
                  max={max}
                  value={draft}
                  onChange={(v) => {
                    if (v !== null) setScore(v)
                  }}
                  aria-label={t('popups.addMaturityScoring.answerScore')}
                />
                <span className="msm-score-max">/ {fmt.decimal(max)}</span>
              </div>
            </div>

            <div className="msm-slider-wrap">
              <input
                className="msm-slider"
                type="range"
                min={min}
                max={max}
                step={0.01}
                value={draft}
                onChange={(e) => setScore(parseFloat(e.target.value))}
                style={{
                  background: `linear-gradient(90deg, #0D9B87 ${pct}%, #DFE7E8 ${pct}%)`,
                }}
                aria-valuemin={min}
                aria-valuemax={max}
                aria-valuenow={draft}
                aria-label={t('popups.addMaturityScoring.answerScore')}
              />
              <div className="msm-slider-ticks">
                {LEVEL_META.filter((l) => l.level <= Math.ceil(max)).map((l) => (
                  <button
                    key={l.level}
                    type="button"
                    className={`msm-tick${levelFloor === l.level ? ' is-active' : ''}${l.level > max ? ' is-disabled' : ''}`}
                    disabled={l.level > max}
                    onClick={() => setScore(Math.min(l.level, max))}
                  >
                    {l.level}
                  </button>
                ))}
              </div>
            </div>

            <div className="msm-level-card">
              <div className="msm-level-badge">
                {t('popups.addMaturityScoring.levelBadge', {
                  level: levelFloor,
                  defaultValue: `Level ${levelFloor}`,
                })}
              </div>
              <div className="msm-level-copy">
                <strong>
                  {t(`popups.addMaturityScoring.answerScoreOptions.${levelKey}`)}
                </strong>
                <p>
                  {t(
                    `popups.addMaturityScoring.answerScoreDescriptions.${levelKey}`
                  )}
                </p>
              </div>
            </div>

            {typeof TargetScore === 'number' && TargetScore > 0 ? (
              <p className="msm-cap-note">
                {t('popups.addMaturityScoring.targetCap', {
                  target: TargetScore,
                  defaultValue: `Capped by this subdomain target score (${TargetScore}).`,
                })}
              </p>
            ) : null}
          </div>

          <div className="msm-proof">
            {displayName ? (
              <div className="msm-proof-file">
                <span className="msm-proof-ico" aria-hidden>
                  <InsertDriveFileOutlinedIcon sx={{ fontSize: 20 }} />
                </span>
                <div className="msm-proof-meta">
                  <strong>{displayName}</strong>
                  <span>
                    {t('maturityScoringPage.proofAttached', {
                      defaultValue: 'Attached maturity proof document',
                    })}
                  </span>
                </div>
                {canView ? (
                  <DetailButton
                    variant="ghost"
                    size="sm"
                    onClick={() => setViewOpen(true)}
                  >
                    <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                    {t('buttons.view', { defaultValue: 'View' })}
                  </DetailButton>
                ) : null}
              </div>
            ) : null}
            <label className="msm-proof-btn">
              <CloudUploadOutlinedIcon sx={{ fontSize: 18 }} />
              {displayName
                ? t('buttons.replace', { defaultValue: 'Replace' })
                : t('popups.addMaturityScoring.uploadProof')}
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleProofInput}
                hidden
              />
            </label>
            {!displayName ? (
              <span className="msm-proof-name">
                {t('popups.addMaturityScoring.noFile', {
                  defaultValue: 'No proof selected yet',
                })}
              </span>
            ) : null}
            {effectiveProof ? (
              <span className="msm-proof-ai-hint">
                {t('popups.addMaturityScoring.aiHint', {
                  defaultValue: 'AI will suggest a score from this proof',
                })}
              </span>
            ) : null}
          </div>

          <div className="msm-foot">
            <DetailButton variant="ghost" onClick={onClose} disabled={submitting}>
              {t('popups.addMaturityScoring.cancel')}
            </DetailButton>
            <DetailButton onClick={handleSubmit} disabled={submitting}>
              {mode === 'edit'
                ? t('popups.addMaturityScoring.edit')
                : t('popups.addMaturityScoring.add')}
            </DetailButton>
          </div>
        </div>
        <DocumentModal
          open={viewOpen}
          onClose={() => setViewOpen(false)}
          document={existingDocument}
        />
      </Dialog>

      <AiMaturitySuggestionModal
        open={aiOpen}
        phase={aiPhase}
        result={aiResult}
        userScore={draft}
        suggestedScore={suggestedScore}
        errorMessage={aiError}
        onClose={() => setAiOpen(false)}
        onAccept={handleAcceptAi}
        onKeep={handleKeepScore}
        onUseAi={handleChooseAi}
        onSaveOnly={handleChooseSkip}
        onRetry={
          effectiveProof
            ? () => {
                void startAnalysis()
              }
            : undefined
        }
        onCancelAnalysis={cancelAnalysis}
        t={t}
      />
    </>
  )
}

export default MaturityScoringModal
